import React, { useState, useEffect } from 'react';
import { 
  Calendar, Plus, Pencil, Trash2, Eye, EyeOff, Star,
  MapPin, Clock, Loader2, Search, Users, Globe, DollarSign
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Capacitación', 'Networking', 'Feria', 'Webinar', 'Taller', 'Conferencia', 'Otro'];

const EventsPage = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    title: '', slug: '', description: '', content: '',
    event_date: '', event_time: '', end_date: '',
    location: '', address: '', map_url: '', image_url: '',
    registration_url: '', max_attendees: '',
    is_online: false, is_free: true, price: '',
    category: 'Evento', is_published: false, is_featured: false, tags: ''
  });

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => title.toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        end_date: formData.end_date || null,
        event_time: formData.event_time || null
      };
      
      if (editingEvent) {
        await axios.put(`${API_URL}/api/events/${editingEvent.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/events`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEvents();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const togglePublish = async (item) => {
    try {
      await axios.post(`${API_URL}/api/events/${item.id}/toggle-publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEvents();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await axios.put(`${API_URL}/api/events/${item.id}`, 
        { is_featured: !item.is_featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEvents();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const openEditModal = (item) => {
    setEditingEvent(item);
    setFormData({
      title: item.title, slug: item.slug, description: item.description,
      content: item.content, event_date: item.event_date?.split('T')[0] || '',
      event_time: item.event_time || '', end_date: item.end_date?.split('T')[0] || '',
      location: item.location, address: item.address || '', map_url: item.map_url || '',
      image_url: item.image_url || '', registration_url: item.registration_url || '',
      max_attendees: item.max_attendees || '', is_online: item.is_online,
      is_free: item.is_free, price: item.price || '', category: item.category,
      is_published: item.is_published, is_featured: item.is_featured,
      tags: item.tags?.join(', ') || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '', slug: '', description: '', content: '',
      event_date: '', event_time: '', end_date: '',
      location: '', address: '', map_url: '', image_url: '',
      registration_url: '', max_attendees: '',
      is_online: false, is_free: true, price: '',
      category: 'Capacitación', is_published: false, is_featured: false, tags: ''
    });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-PY', {day:'numeric',month:'short',year:'numeric'}) : '';
  const isPast = (d) => d ? new Date(d) < new Date() : false;

  const filteredEvents = events.filter(e => 
    e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-6" data-testid="events-admin-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Eventos</h1>
          <p className="text-zinc-500">Gestiona los eventos del sitio web</p>
        </div>
        <button onClick={() => { resetForm(); setEditingEvent(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
          <Plus className="w-5 h-5" /> Nuevo Evento
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar eventos..." className="w-full pl-10 pr-4 py-2 border border-zinc-300 rounded-lg" />
      </div>

      {filteredEvents.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <Calendar className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-zinc-900 mb-2">No hay eventos</h3>
          <p className="text-zinc-500">Crea tu primer evento</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredEvents.map((item) => (
            <div key={item.id} className={`bg-white rounded-xl shadow-sm border p-4 ${!item.is_published ? 'opacity-70' : ''}`}>
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 bg-zinc-100 rounded-lg flex-shrink-0 overflow-hidden">
                  {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> 
                    : <Calendar className="w-8 h-8 text-zinc-300 m-auto mt-8" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-zinc-900">{item.title}</h3>
                    {item.is_featured && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1"><Star className="w-3 h-3" /> Destacado</span>}
                    {item.is_online && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1"><Globe className="w-3 h-3" /> Online</span>}
                    {isPast(item.event_date) && <span className="px-2 py-0.5 bg-zinc-100 text-zinc-500 text-xs rounded-full">Pasado</span>}
                  </div>
                  <p className="text-sm text-zinc-500 line-clamp-1 mb-2">{item.description}</p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(item.event_date)}</span>
                    {item.event_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.event_time}</span>}
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>
                    {!item.is_free && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{item.price}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleFeatured(item)} className={`p-2 rounded-lg ${item.is_featured ? 'bg-yellow-100 text-yellow-600' : 'bg-zinc-100 text-zinc-400'}`}><Star className="w-5 h-5" /></button>
                  <button onClick={() => togglePublish(item)} className={`p-2 rounded-lg ${item.is_published ? 'bg-emerald-100 text-emerald-600' : 'bg-zinc-100 text-zinc-400'}`}>
                    {item.is_published ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button onClick={() => openEditModal(item)} className="p-2 bg-zinc-100 text-zinc-600 rounded-lg hover:bg-zinc-200"><Pencil className="w-5 h-5" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"><Trash2 className="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b"><h2 className="text-xl font-bold">{editingEvent ? 'Editar Evento' : 'Nuevo Evento'}</h2></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Título *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value, slug: editingEvent ? formData.slug : generateSlug(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Slug (URL)</label>
                  <input type="text" value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Categoría</label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Descripción corta *</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2} className="w-full px-4 py-2 border rounded-lg" required />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Contenido completo (HTML)</label>
                <textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={5} className="w-full px-4 py-2 border rounded-lg font-mono text-sm" />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Fecha del evento *</label>
                  <input type="date" value={formData.event_date} onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Hora</label>
                  <input type="text" value={formData.event_time} onChange={(e) => setFormData({...formData, event_time: e.target.value})}
                    placeholder="18:00" className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Fecha fin (opcional)</label>
                  <input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Ubicación *</label>
                  <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Asunción, Paraguay" className="w-full px-4 py-2 border rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Dirección exacta</label>
                  <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Imagen</label>
                <ImageUploader currentImage={formData.image_url} onImageUploaded={(url) => setFormData({...formData, image_url: url})} folder="events" />
                <input type="url" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="O URL de imagen..." className="w-full px-4 py-2 border rounded-lg mt-2" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">URL de registro</label>
                  <input type="url" value={formData.registration_url} onChange={(e) => setFormData({...formData, registration_url: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Cupo máximo</label>
                  <input type="number" value={formData.max_attendees} onChange={(e) => setFormData({...formData, max_attendees: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>

              <div className="flex flex-wrap gap-4 p-4 bg-zinc-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_online} onChange={(e) => setFormData({...formData, is_online: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">Evento online</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_free} onChange={(e) => setFormData({...formData, is_free: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">Gratuito</span>
                </label>
                {!formData.is_free && (
                  <input type="text" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="Precio" className="px-3 py-1 border rounded-lg text-sm w-32" />
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({...formData, is_published: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">Publicado</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData({...formData, is_featured: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">Destacado</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => {setShowModal(false); setEditingEvent(null);}} className="px-4 py-2 text-zinc-700 hover:bg-zinc-100 rounded-lg">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingEvent ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
